import { getExtensionValue, ProfileResource } from "@medplum/core";

import { Extensions } from "@/fhir/enums/extensions";

import { NO_NOTIFICATIONS } from "../types/NotificationsPreference";

/**
 * Checks if a resource has opted in to receive a specific notification type.
 *
 * @param resource - The profile resource to check preferences for
 * @param notificationId - The notification preference ID to check (e.g., 'appointment-confirmation', 'messages-notifications')
 * @returns true if the resource has opted in (preference is included), false otherwise
 */
export const hasOptedInToNotification = (
  resource: ProfileResource,
  notificationId: string,
): boolean => {
  const preferencesValue = getExtensionValue(resource, Extensions.NotificationPreferences) as
    | string
    | undefined;

  if (!preferencesValue) return true;

  const preferences = Array.isArray(preferencesValue)
    ? preferencesValue
    : preferencesValue
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

  if (!preferences.length) return true;

  if (preferences.includes(NO_NOTIFICATIONS)) return false;

  return preferences.includes(notificationId);
};

/**
 * Checks if a resource has opted out of a specific notification type.
 *
 * @param resource - The profile resource to check preferences for
 * @param notificationId - The notification preference ID to check (e.g., 'appointment-confirmation', 'messages-notifications')
 * @returns true if the resource has opted out (preference is NOT included), false otherwise
 */
export const hasOptedOutOfNotification = (
  resource: ProfileResource,
  notificationId: string,
): boolean => {
  return !hasOptedInToNotification(resource, notificationId);
};
